// React and MUI imports
import * as React from 'react';
import {
  Breadcrumbs,
  Box,
  Link,
  styled,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useNavigate } from 'react-router-dom';


// Type for a single breadcrumb item
interface BreadCrumbType {
  text: string;
  href: string;
}

/**
 * Props for the HDBreadCrumb component
 * @property underline - underline style for links
 * @property routes - array of breadcrumb items
 * @property handleClick - optional click handler for breadcrumb item
 * @property gaTagEvents - optional array of GA event handlers
 */
export interface HDBreadCrumbTypes {
  underline?: 'none' | 'hover' | 'always';
  routes: Array<BreadCrumbType>;
  handleClick?: any;
  gaTagEvents?: Array<() => void>;
}

/**
 * HDBreadCrumb - Responsive breadcrumb navigation component.
 * Renders a back button on mobile and a breadcrumb trail on desktop.
 * @param underline - underline style for links
 * @param routes - array of breadcrumb items
 * @param gaTagEvents - optional array of GA event handlers
 * @param handleClick - optional click handler for breadcrumb item
 */
const HDBreadCrumb = ({ underline = 'always', routes, gaTagEvents, handleClick }: HDBreadCrumbTypes): any | null => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  let navigate = useNavigate();

  // Styled wrapper for the breadcrumb
  const StyledBreadCrumbBox = styled(Box)(() => ({
    display: 'block',
  }));

  // Styled container for the mobile back button
  const StyledBackBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
  }));

  // Styled text for the back button
  const StyledBackText = styled(Typography)(() => ({
    fontFamily: 'Avenir',
    fontWeight: 300,
    fontSize: 16,
    lineHeight: '24px',
    color: isDark ? '#ccc' : '#334255',
    marginTop: '1.5px',
    cursor: 'pointer',
  }));

  // Styled left arrow icon for back button
  const StyledArrowLeftIcon = styled(KeyboardArrowLeftIcon)(() => ({
    width: 28,
    height: 28,
    color: isDark ? '#fff' : '#002136',
    marginRight: '-1px',
    cursor: 'pointer',
  }));

  // Mobile: show back button
  if (isMobile) {
    return (
      <StyledBackBox>
        <StyledArrowLeftIcon />
        <Link underline='none'>
          <StyledBackText onClick={() => history.back()}>Back</StyledBackText>
        </Link>
      </StyledBackBox>
    );
  }

  // Handler for clicking a breadcrumb item
  const onItemClick = (el: any, index: any) => {
    if (handleClick) handleClick(el);
    if (gaTagEvents && gaTagEvents[index]) gaTagEvents[index]();
    navigate(el.href);
  };

  // Desktop: show breadcrumb trail
  return (
    <StyledBreadCrumbBox>
      <Breadcrumbs aria-label='breadcrumb'>
        {routes?.map((el, index) => {
          // Last item: render as plain text or link
          if (index === (routes.length - 1)) {
            if (el.href === '#') {
              return (
                <div key={`bread_crumb_${el.text}`}>
                  {el.text}
                </div>
              );
            } else {
              return (
                <Link
                  sx={{ cursor: 'pointer' }}
                  key={`bread_crumb_${el.text}`}
                  underline='none'
                  color='inherit'
                  // href={el.href}
                  onClick={() => onItemClick(el, index)}
                >
                  {el.text}
                </Link>
              );
            }
          }

          // Intermediate items: render as link or plain text
          if (el.href === '#') {
            return (
              <div key={`bread_crumb_${el.text}`}>
                {el.text}
              </div>
            );
          } else {
            return (
              <Link
                sx={{ cursor: 'pointer' }}
                key={`bread_crumb_${el.text}`}
                underline={underline}
                color='inherit'
                // href={el.href}
                onClick={() => onItemClick(el, index)}
              >
                {el.text}
              </Link>
            );
          }
        })}
      </Breadcrumbs>
    </StyledBreadCrumbBox>
  );
};

export default HDBreadCrumb;
